import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, LifeBuoy, Mail, MessageSquare, Book, ShieldQuestion } from "lucide-react";

const FAQ = [
  { q: "How does Try-On work?", a: "Upload one or two clear identity photos (front-facing, neutral background) to AI Identity & Style. Our AI then composites you into any outfit you tap 'Try on'. Better photos = better matches." },
  { q: "How do I make my profile 100%?", a: "Open Personal details — every blank field shows a 'MISSING' badge with a one-line hint. Saving even a few fields meaningfully improves outfit & sizing accuracy." },
  { q: "How do I delete my data?", a: "Profile → Privacy & Security → 'Request data export' for a copy, then Profile → Delete account for full removal. We comply within 24 hours." },
  { q: "Can I have multiple accounts?", a: "Yes — tap your name+avatar at the top of Profile to open the account switcher. Add or switch between accounts in two taps." },
  { q: "Where can I see what's coming next?", a: "Profile → Plans (or the 'Coming soon' card on Home) leads to the public roadmap." },
];

export default function HelpPage() {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><LifeBuoy size={12} /> Stylebay · Help</p>
      <h1 className="editorial-h1 mt-3">How can we help?</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">
        Quick answers, or reach a human in one tap.
      </p>

      {/* Contact actions */}
      <div className="mt-10 grid sm:grid-cols-2 gap-3">
        <a
          href="mailto:contact@stylebay.co.in?subject=Stylebay%20Support"
          data-testid="help-email-btn"
          className="border border-border p-5 hover:border-foreground transition-colors flex items-start gap-3"
        >
          <span className="h-10 w-10 inline-flex items-center justify-center border border-border shrink-0"><Mail size={16} /></span>
          <span>
            <span className="block text-sm font-medium">Email support</span>
            <span className="block text-xs text-muted-foreground mt-0.5">contact@stylebay.co.in · 24h response</span>
          </span>
        </a>
        <button
          onClick={() => navigate("/feedback?from=help")}
          data-testid="help-feedback-btn"
          className="border border-border p-5 hover:border-foreground transition-colors flex items-start gap-3 text-left"
        >
          <span className="h-10 w-10 inline-flex items-center justify-center border border-border shrink-0"><MessageSquare size={16} /></span>
          <span>
            <span className="block text-sm font-medium">Send feedback</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Bugs, ideas, or just say hi</span>
          </span>
        </button>
      </div>

      {/* FAQ */}
      <section className="mt-10">
        <p className="eyebrow flex items-center gap-2"><Book size={12} /> Frequently asked</p>
        <ul className="mt-5 divide-y divide-border border-y border-border" data-testid="help-faq-list">
          {FAQ.map((row, i) => (
            <li key={i} className="py-4">
              <details>
                <summary className="cursor-pointer text-sm font-medium list-none flex items-center justify-between gap-2">
                  <span>{row.q}</span>
                  <span className="text-muted-foreground text-xs">+</span>
                </summary>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{row.a}</p>
              </details>
            </li>
          ))}
        </ul>
      </section>

      {/* Legal */}
      <section className="mt-12 border-t border-border pt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
        <ShieldQuestion size={12} />
        <Link to="/terms" data-testid="help-terms" className="hover:text-foreground">Terms</Link>
        <Link to="/privacy" data-testid="help-privacy" className="hover:text-foreground">Privacy</Link>
        <Link to="/grievance" data-testid="help-grievance" className="hover:text-foreground">Grievance officer</Link>
        <span>v1 · {new Date().getFullYear()}</span>
      </section>
    </div>
  );
}
